import { IntlShape } from 'react-intl'

import {
  MAKE_SOURCE_TYPE,
  MAKE_UNIVERSAL_OPTION
} from 'app/utils/constants/listing.constants'
import { messages } from 'app/utils/constants/options.constants'
import Base64 from 'app/utils/helpers/base64.helpers'
import imageLoader from 'app/utils/helpers/imageLoader'

interface GetListingMetaTitleParams {
  formatMessage: IntlShape['formatMessage']
  condition?: ListingModel['condition']
  itemType?: ListingModel['item_type']
  partNumber?: ListingModel['part_number']
  makes?: ListingModel['makes']
  models?: ListingModel['models']
  subCategory: ListingModel['sub_category']['name']
}

interface FormatTitleParams
  extends Pick<
    GetListingMetaTitleParams,
    'makes' | 'models' | 'formatMessage'
  > {}

const formatTitle = ({ formatMessage, makes, models }: FormatTitleParams) => {
  const hasMultipleFitment =
    Number(makes?.length) > 1 || Number(models?.length) > 1
  const isUniversal = makes?.[0]?.source === MAKE_SOURCE_TYPE.system
  const isMakeModelTitle = !hasMultipleFitment && !isUniversal

  const basicTitle = isMakeModelTitle
    ? `${makes?.[0]?.name} ${models?.[0]?.name}`
    : ''
  const withMultipleFitmentTitle = hasMultipleFitment
    ? formatMessage({ defaultMessage: 'Multiple Fitment' })
    : basicTitle
  const formattedTitle = isUniversal
    ? MAKE_UNIVERSAL_OPTION
    : withMultipleFitmentTitle

  return formattedTitle.trim()
}

const getListingMetaTitle = ({
  formatMessage,
  condition,
  itemType,
  partNumber,
  makes,
  models,
  subCategory
}: GetListingMetaTitleParams) => {
  const listingTitle = formatTitle({ formatMessage, makes, models })

  const listingConditionMessage =
    condition === 'used_item' ? messages.used : messages.new
  const listingCondition = formatMessage(listingConditionMessage)
  const listingType = formatMessage(messages[itemType ?? 'aftermarket'])

  const metaTitle = `${listingTitle} ${subCategory} ${listingCondition} ${listingType} ${partNumber}`

  return metaTitle.trim()
}

interface GetListingMetaDescriptionParams extends GetListingMetaTitleParams {
  generations?: ListingModel['generations']
}

const getListingMetaDescription = ({
  formatMessage,
  makes,
  models,
  subCategory,
  partNumber,
  itemType,
  condition,
  generations
}: GetListingMetaDescriptionParams) => {
  const title = formatTitle({ formatMessage, makes, models })

  const listingType = formatMessage(messages[itemType ?? 'aftermarket'])

  const listingPartNumber = !!partNumber
    ? formatMessage(
        { defaultMessage: ', part number {partNumber}' },
        { partNumber }
      )
    : ''
  const listingConditionMessage =
    condition === 'used_item' ? messages.used : messages.new
  const listingCondition = formatMessage(
    { defaultMessage: '{listingConditionMessage} condition' },
    { listingConditionMessage: formatMessage(listingConditionMessage) }
  )

  const generationYears = generations?.[0]?.year_begin
    ? `${generations[0].year_begin || ''} - ${generations[0].year_end || ''}`
    : formatMessage({ defaultMessage: 'Multiple Years' })
  const fitYears =
    generations?.length === 1
      ? formatMessage(
          { defaultMessage: 'fits {generationYears}' },
          { generationYears }
        )
      : formatMessage({ defaultMessage: 'fits Multiple Years' })

  const buyNow = formatMessage({ defaultMessage: 'Buy now on' })

  const metaDescription = `${listingType} ${title} ${subCategory}${listingPartNumber}. ${listingCondition}, ${fitYears}. ${buyNow} ifndautoparts.`

  return metaDescription.trim()
}

export const getListingMetaImages = ({
  attachments,
  width = 384
}: {
  attachments?: ListingModel['attachments']
  width?: number
}) =>
  attachments?.map((item) => {
    const baseSrc = Base64.btoa(String(item.url))

    return { ...item, url: imageLoader({ src: baseSrc, width }) }
  })

interface GetListingMetaDataParams {
  listing?: ListingModel
  formatMessage: IntlShape['formatMessage']
}

export const getListingMetaData = ({
  listing,
  formatMessage
}: GetListingMetaDataParams) => {
  if (!listing?.id)
    return { metaTitle: '', metaDescription: '', metaImages: [] }

  const metaTitle = getListingMetaTitle({
    formatMessage,
    subCategory: listing?.sub_category?.name || '',
    condition: listing?.condition,
    itemType: listing?.item_type,
    partNumber: listing?.part_number || '',
    makes: listing?.makes,
    models: listing?.models
  })

  const metaDescription = getListingMetaDescription({
    formatMessage,
    makes: listing?.makes,
    models: listing?.models,
    subCategory: listing?.sub_category?.name || '',
    partNumber: listing?.part_number,
    itemType: listing?.item_type,
    condition: listing?.condition,
    generations: listing?.generations
  })

  const metaImages = getListingMetaImages({ attachments: listing?.attachments })

  return { metaTitle, metaDescription, metaImages }
}
